import { useEffect, useMemo } from 'react';
import {
  Card, Group, Text, Title, Badge, Stack, Center, Avatar, Box, ActionIcon,
  Button, TextInput, PasswordInput, Select, Modal, SimpleGrid, ThemeIcon, Divider, Tabs,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft, IconCrown, IconPencil, IconUsers, IconGift, IconMessageCircle, IconSchool,
  IconShieldCheck, IconAddressBook, IconScale, IconLayoutList, IconCash,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { formatPhone, formatDate, formatSom } from '../lib/format';
import { monthLabel, thisMonth } from '../lib/month';
import type { StudentBalancesResp } from '../lib/balance';
import { BalanceStatusBadge } from '../components/BalanceStatusBadge';
import { ContactsManager } from '../components/ContactsManager';
import { StaffDeleteButton } from '../components/StaffDeleteButton';
import { BlockControl } from '../components/BlockControl';
import { PREMIUM_UI_ENABLED } from '../lib/features';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';
import { uzDateProps } from '../lib/dateInput';
import { ParentPayments } from '../components/ParentPayments';

interface Child { id: string; fullName: string; status: string; class?: { id: string; name: string } | null }
interface ParentFull {
  id: string; login: string; fullName: string; phone?: string | null; gender?: 'MALE' | 'FEMALE' | null;
  birthDate?: string | null;
  isActive: boolean; blockReason?: string | null; premiumUntil?: string | null; premiumBadge?: string | null; createdAt: string;
  /** S3 kaliti yoki `sticker:<...>` — ota-ona ilovadan yuklaydi (`/premium/parents/:id` qaytaradi) */
  avatar?: string | null;
  phones?: { id: string; label: string; number: string; isPrimary: boolean }[];
  addresses?: { id: string; label: string; address: string; isPrimary: boolean }[];
  children: Child[];
  _count: { giftsSent: number; giftsReceived: number; supportMessages: number };
}

const isPremium = (until?: string | null) => !!until && new Date(until) > new Date();

/**
 * Ota-ona kartochkasi.
 *
 * PREMIUM MUZLATILGAN (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`): premium
 * nishoni, "Obuna" kartasi va sovg'a ko'rsatkichlari yashirilgan. Qolgani
 * (tahrirlash, bloklash, farzandlar, kontaktlar) o'z holicha ishlaydi — backend
 * `PATCH /premium/parents/:id` faqat premium maydonlari yuborilsa 403 qaytaradi,
 * bu sahifa esa ularni yubormaydi.
 */
export default function ParentDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const qc = useQueryClient();
  const { user } = useAuth();
  // Balans FINANCE endpointidan keladi — ruxsati yo'q rol uchun umuman so'ralmaydi.
  const canFinance = canAccess(user?.role, 'payments');
  const navigate = useNavigate();
  const [editOpen, editH] = useDisclosure(false);
  /* Tab URL'da (`?tab=payments`) — havola ulashilsa yoki sahifa yangilansa ham o'sha
     tab ochiladi. To'lovlar tabi faqat moliya ruxsati bor rolga. */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = canFinance && searchParams.get('tab') === 'payments' ? 'payments' : 'overview';
  const setTab = (v: string | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'payments') next.set('tab', 'payments');
      else next.delete('tab');
      return next;
    }, { replace: true });

  const { data: parent, isLoading } = useQuery({
    queryKey: ['parent', id],
    queryFn: async () => (await api.get<ParentFull>(`/premium/parents/${id}`)).data,
    enabled: !!id,
  });

  const editForm = useForm({
    initialValues: { fullName: '', phone: '', gender: '' as string, birthDate: null as Date | null, login: '', password: '' },
    validate: {
      login: (v) => (v.trim().length < 3 ? t('parentDetail.loginMin') : null),
      // Bo'sh = parol tegilmaydi. Chegara panel bo'ylab bir xil (5) — server 4 dan
      // boshlab qabul qiladi, ya'ni front qat'iyroq, hech qachon rad javob keltirmaydi.
      password: (v) => (v && v.length < 5 ? t('parentDetail.passwordMin') : null),
    },
  });
  useEffect(() => {
    if (parent) editForm.setValues({ fullName: parent.fullName, phone: parent.phone ?? '', gender: parent.gender ?? '', birthDate: parent.birthDate ? new Date(parent.birthDate) : null, login: parent.login, password: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent?.id]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['parent', id] });

  const editMut = useMutation({
    // Parol bo'sh bo'lsa maydon UMUMAN yuborilmaydi — aks holda server uni
    // "yangi parol" deb qabul qilib, har saqlashda hisobni qulflab qo'yardi.
    mutationFn: ({ password, ...v }: typeof editForm.values) =>
      api.patch(`/premium/parents/${id}`, {
        ...v,
        birthDate: v.birthDate ? v.birthDate.toISOString() : null,
        ...(password ? { password } : {}),
      }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('common.save') }); invalidate(); qc.invalidateQueries({ queryKey: ['parents-page'] }); editH.close(); },
  });

  // FARZANDLAR BALANSI (#66) — joriy oy uchun bitta so'rovda (Moliya jadvali bilan
  // AYNI manba: `GET /reports/balances/students`). Faqat FAOL o'quvchilar qatori
  // qaytadi — qolganlari uchun "ma'lumot yo'q" ko'rsatiladi.
  const balanceMonth = thisMonth();
  const childIds = (parent?.children ?? []).map((c) => c.id).join(',');
  const { data: childBalances } = useQuery({
    queryKey: ['parent-child-balances', childIds, balanceMonth],
    queryFn: async () =>
      (await api.get<StudentBalancesResp>('/reports/balances/students', {
        params: { month: balanceMonth, ids: childIds },
      })).data,
    enabled: !!childIds && canFinance,
  });
  const balanceByChild = useMemo(
    () => new Map((childBalances?.items ?? []).map((r) => [r.studentId, r])),
    [childBalances]
  );

  if (isLoading) return <BlockLoader variant="card" />;
  if (!parent) return <Center h={300}><Text c="dimmed">{t('parentDetail.notFound')}</Text></Center>;

  const genderLabel = parent.gender === 'MALE' ? t('parentDetail.father') : parent.gender === 'FEMALE' ? t('parentDetail.mother') : '—';

  return (
    <Stack>
      {/* Sarlavha */}
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/parents')}><IconArrowLeft size={18} /></ActionIcon>
        <Card style={{ flex: 1 }} p="md">
          <Group justify="space-between">
            <Group>
              {/* fileUrl stiker (`sticker:<...>`) uchun undefined qaytaradi — bosh harf ko'rinadi */}
              <Avatar color="grape" radius="xl" size={54} src={fileUrl(parent.avatar)} alt={parent.fullName}>
                {parent.fullName[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Group gap="xs">
                  <Title order={4}>{parent.fullName}</Title>
                  <Badge variant="light" color={parent.gender === 'MALE' ? 'teal' : parent.gender === 'FEMALE' ? 'pink' : 'grape'}>{genderLabel}</Badge>
                  {PREMIUM_UI_ENABLED && isPremium(parent.premiumUntil) && <Badge color="yellow" leftSection={<IconCrown size={12} />}>Premium</Badge>}
                  {!parent.isActive && <Badge color="gray">{t('parentDetail.blocked')}</Badge>}
                </Group>
                <Group gap="xs" mt={2}>
                  <Text size="sm" c="dimmed" ff="monospace">{parent.login}</Text>
                  <Text size="sm" c="dimmed">{formatPhone(parent.phone)}</Text>
                </Group>
              </Box>
            </Group>
            <Group gap="xs">
              <Button leftSection={<IconPencil size={16} />} onClick={editH.open}>{t('parentDetail.edit')}</Button>
              <StaffDeleteButton id={parent.id} fullName={parent.fullName} backTo="/parents" invalidateKeys={['parents-page']} />
            </Group>
          </Group>
        </Card>
      </Group>

      {/* Ko'rsatkichlar — sovg'alar premium bilan birga yashiriladi,
          shu sababli ustunlar soni ham qolgan kartochkalarga moslanadi (bo'sh joy qolmasin) */}
      <SimpleGrid cols={{ base: 2, md: PREMIUM_UI_ENABLED ? 4 : 2 }}>
        <StatMini icon={IconUsers} color="blue" label={t('parentDetail.children')} value={parent.children.length} />
        {PREMIUM_UI_ENABLED && <StatMini icon={IconGift} color="grape" label={t('parentDetail.giftsSent')} value={parent._count.giftsSent} />}
        {PREMIUM_UI_ENABLED && <StatMini icon={IconGift} color="teal" label={t('parentDetail.giftsReceived')} value={parent._count.giftsReceived} />}
        <StatMini icon={IconMessageCircle} color="cyan" label={t('parentDetail.messages')} value={parent._count.supportMessages} />
      </SimpleGrid>

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconLayoutList size={16} />}>{t('parentDetail.tabOverview')}</Tabs.Tab>
          {canFinance && (
            <Tabs.Tab value="payments" leftSection={<IconCash size={16} />}>{t('parentDetail.tabPayments')}</Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="overview">
          <Stack>
            {/* Premium holati */}
            {PREMIUM_UI_ENABLED && (
              <SectionCard
                title={t('parentDetail.subscription')}
                icon={IconCrown}
                color="yellow"
                p={0}
                right={isPremium(parent.premiumUntil)
                  ? <Badge color="yellow" variant="light" size="lg">{formatDate(parent.premiumUntil!)} {t('parentDetail.until')}</Badge>
                  : <Badge color="gray" variant="light" size="lg">{t('parentDetail.free')}</Badge>}
              />
            )}

            {/* Holat / bloklash */}
            <SectionCard title={t('teacherDetail.status')} icon={IconShieldCheck}>
              <BlockControl
                patchPath={`/premium/parents/${id}`}
                isActive={parent.isActive}
                blockReason={parent.blockReason}
                onDone={() => { invalidate(); qc.invalidateQueries({ queryKey: ['parents-page'] }); }}
              />
            </SectionCard>

            {/* Farzandlar */}
            <SectionCard
              title={t('parentDetail.children')}
              icon={IconUsers}
              // Balans QAYSI oy uchun ekani sarlavhada — raqamlar sanasiz o'qilmasdi
              subtitle={canFinance ? t('parentDetail.balanceMonth', { month: monthLabel(childBalances?.month ?? balanceMonth, monthNames) }) : undefined}
              right={<Badge variant="light" color="gray">{parent.children.length}</Badge>}
            >
              {!parent.children.length ? (
                <Text c="dimmed" size="sm">{t('parentDetail.noChildren')}</Text>
              ) : (
                <Stack gap="xs">
                  {parent.children.map((c) => {
                    const bal = balanceByChild.get(c.id);
                    return (
                      <Group key={c.id} justify="space-between" p="xs" gap="sm" wrap="wrap"
                        style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => navigate(`/students/${c.id}`)}>
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <ThemeIcon variant="light" color="blue" size={34} radius="md"><IconSchool size={18} /></ThemeIcon>
                          <Box style={{ minWidth: 0 }}>
                            <Text fw={600} size="sm">{c.fullName}</Text>
                            <Text size="xs" c="dimmed">{c.class?.name ?? '—'}</Text>
                          </Box>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          {canFinance && (bal ? (
                            <Group gap={6} wrap="nowrap">
                              <IconScale size={14} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} />
                              <Text size="xs" fw={700} c={bal.balance < 0 ? 'red.7' : bal.balance > 0 ? 'blue.7' : 'dimmed'}>
                                {bal.balance < 0 ? `−${formatSom(bal.debt)}` : bal.balance > 0 ? `+${formatSom(bal.overpaid)}` : '—'}
                              </Text>
                              <BalanceStatusBadge status={bal.status} size="sm" />
                            </Group>
                          ) : (
                            <Text size="xs" c="dimmed">{t('parentDetail.balanceNone')}</Text>
                          ))}
                          <Badge variant="light" color={c.status === 'ACTIVE' ? 'teal' : 'gray'}>{t(`studentStatus.${c.status}`)}</Badge>
                        </Group>
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </SectionCard>

            {/* Kontaktlar */}
            <SectionCard title={t('parentDetail.contacts')} icon={IconAddressBook}>
              <ContactsManager owner={{ userId: parent.id }} phones={parent.phones ?? []} addresses={parent.addresses ?? []} queryKey={['parent', id]} />
            </SectionCard>
          </Stack>
        </Tabs.Panel>

        {canFinance && (
          <Tabs.Panel value="payments">
            <Stack>
              <ParentPayments parentId={parent.id} childIds={parent.children.map((c) => c.id)} />
            </Stack>
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Tahrirlash modali */}
      <Modal opened={editOpen} onClose={editH.close} title={t('parentDetail.edit')}>
        <form onSubmit={editForm.onSubmit((v) => editMut.mutate(v))}>
          <Stack>
            <TextInput label={t('parentDetail.fullName')} required {...editForm.getInputProps('fullName')} />
            <TextInput label={t('parentDetail.phone')} placeholder="+998..." {...editForm.getInputProps('phone')} />
            <Select
              label={t('parentDetail.gender')} allowDeselect={false}
              data={[{ value: 'MALE', label: t('parentDetail.father') }, { value: 'FEMALE', label: t('parentDetail.mother') }]}
              {...editForm.getInputProps('gender')}
            />
            <DateInput label={t('parentDetail.birthDate')} {...uzDateProps} {...editForm.getInputProps('birthDate')} />
            <Divider label={t('parentDetail.account')} labelPosition="left" />
            <TextInput label={t('parentDetail.login')} required {...editForm.getInputProps('login')} />
            <PasswordInput
              label={t('parentDetail.newPassword')}
              description={t('parentDetail.passwordHint')}
              autoComplete="new-password"
              {...editForm.getInputProps('password')}
            />
            <Divider />
            <Group justify="flex-end">
              <Button variant="default" onClick={editH.close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={editMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

function StatMini({ icon: Ico, color, label, value }: { icon: typeof IconUsers; color: string; label: string; value: number }) {
  return (
    <Card p="md">
      <Group>
        <ThemeIcon size={40} radius="md" variant="light" color={color}><Ico size={22} /></ThemeIcon>
        <Box>
          <Text fz={22} fw={800} lh={1}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </Box>
      </Group>
    </Card>
  );
}
